export interface ThemeColors {
  // Base colors
  base: string;
  base_hover: string;
  base_active: string;
  base_border: string;
  base_subtle: string;
  base_primary: string;
  base_error: string;
  
  // Accent colors
  accent: string;
  accent_hover: string;
  accent_active: string;
  accent_border: string;
  accent_subtle: string;
  accent_primary: string;
  
  // Avatar colors
  avatar_first_color: string;
  avatar_second_color: string;
}

export interface ThemeSpacing {
  overlay_padding: number;
  button_radius: number;
  input_radius: number;
  bubble_radius: number;
  sheet_radius: number;
  compact_sheet_radius: number;
  dropdown_sheet_radius: number;
}

export interface ThemeTypography {
  font_family: string;
  font_size_small: number;
  font_size_medium: number;
  font_size_large: number;
  font_weight_normal: number;
  font_weight_medium: number;
  font_weight_bold: number;
}

export interface ThemeConfig {
  colors: ThemeColors;
  spacing: ThemeSpacing;
  typography: ThemeTypography;
  options?: ThemeOptions;
}

export interface ThemeOptions {
  show_close_button?: boolean;
}

import { SupportedLanguage } from "@/shared/language";

export interface TextContent {
  main_label: string;
  start_call: string;
  start_chat: string;
  new_call: string;
  end_call: string;
  mute_microphone: string;
  change_language: string;
  collapse: string;
  expand: string;
  copied: string;
  accept_terms: string;
  dismiss_terms: string;
  listening_status: string;
  speaking_status: string;
  connecting_status: string;
  chatting_status: string;
  input_label: string;
  input_placeholder: string;
  input_placeholder_text_only: string;
  input_placeholder_new_conversation: string;
  user_ended_conversation: string;
  agent_ended_conversation: string;
  conversation_id: string;
  error_occurred: string;
  microphone_access_required: string;
  copy_id: string;
  microphone_blocked_message: string;
  microphone_unavailable_title: string;
  microphone_unavailable_message: string;
  refresh_button_label: string;
  input_device_label: string;
  input_device_label_connected: string;
  output_device_label: string;
  output_device_label_connected: string;
  select_device_placeholder: string;
  input_device_changed_title: string;
  input_device_changed_message: string;
  input_device_change_failed_title: string;
  input_device_change_failed_message: string;
  output_device_changed_title: string;
  output_device_changed_message: string;
  output_device_change_failed_title: string;
  output_device_change_failed_message: string;
  send_failed_title: string;
  send_not_supported_message: string;
  send_failed_generic_message: string;
  microphone_muted_title: string;
  microphone_muted_message: string;
  connection_failed_title: string;
  connection_failed_message: string;
  connection_error_title: string;
  connection_error_message: string;
  greeting_with_agent: string;
  greeting_without_agent: string;
}

export interface WidgetTheme {
  config: ThemeConfig;
  textContent: TextContent;
}

// Default theme configuration
export const DEFAULT_THEME: ThemeConfig = {
  colors: {
    base: '#ffffff',
    base_hover: '#f9fafb',
    base_active: '#f3f4f6',
    base_border: '#e5e7eb',
    base_subtle: '#6b7280',
    base_primary: '#000000',
    base_error: '#ef4444',
    accent: '#6366f1',
    accent_hover: '#4f46e5',
    accent_active: '#4338ca',
    accent_border: '#4b5563',
    accent_subtle: '#6b7280',
    accent_primary: '#ffffff',
    avatar_first_color: '#6366f1',
    avatar_second_color: '#8b5cf6',
  },
  spacing: {
    overlay_padding: 16,
    button_radius: 32,
    input_radius: 10,
    bubble_radius: 15,
    sheet_radius: 24,
    compact_sheet_radius: 30,
    dropdown_sheet_radius: 16,
  },
  typography: {
    font_family: 'Inter, system-ui, sans-serif',
    font_size_small: 12,
    font_size_medium: 14,
    font_size_large: 16,
    font_weight_normal: 400,
    font_weight_medium: 500,
    font_weight_bold: 600,
  },
  options: {
    show_close_button: true,
  },
};

export const DEFAULT_TEXT_CONTENT: TextContent = {
  main_label: 'Need help?',
  start_call: 'Start a call',
  start_chat: 'Start a chat',
  new_call: 'New call',
  end_call: 'End',
  mute_microphone: 'Mute microphone',
  change_language: 'Change language',
  collapse: 'Collapse',
  expand: 'Expand',
  copied: 'Copied!',
  accept_terms: 'Accept',
  dismiss_terms: 'Cancel',
  listening_status: 'Listening',
  speaking_status: 'Talk to interrupt',
  connecting_status: 'Connecting',
  chatting_status: 'Chatting with AI Agent',
  input_label: 'Text message input',
  input_placeholder: 'Send a message',
  input_placeholder_text_only: 'Send a message',
  input_placeholder_new_conversation: 'Start a new conversation',
  user_ended_conversation: 'You ended the conversation',
  agent_ended_conversation: 'Agent ended the conversation',
  conversation_id: 'Conversation ID',
  error_occurred: 'An error occurred',
  microphone_access_required: 'Microphone Access Required',
  copy_id: 'Copy ID',
  microphone_blocked_message: 'Microphone access is blocked. Please click the microphone icon in your browser\'s address bar and allow access, then refresh the page.',
  microphone_unavailable_title: 'Microphone not available',
  microphone_unavailable_message: 'Microphone not available. Please ensure a microphone is connected and enabled, then try again.',
  refresh_button_label: 'Refresh Page',
  input_device_label: 'Input Device',
  input_device_label_connected: 'Input',
  output_device_label: 'Output Device',
  output_device_label_connected: 'Output',
  select_device_placeholder: 'Select device',
  input_device_changed_title: 'Device Changed',
  input_device_changed_message: 'Input device has been changed successfully.',
  input_device_change_failed_title: 'Device Change Failed',
  input_device_change_failed_message: 'Failed to switch audio input device.',
  output_device_changed_title: 'Device Changed',
  output_device_changed_message: 'Output device has been changed successfully.',
  output_device_change_failed_title: 'Device Change Failed',
  output_device_change_failed_message: 'Failed to switch audio output device.',
  send_failed_title: 'Send Failed',
  send_not_supported_message: 'Text messaging is not supported during voice calls.',
  send_failed_generic_message: 'Failed to send message. Please try again.',
  microphone_muted_title: 'Microphone Muted',
  microphone_muted_message: 'Your microphone has been muted. Please check your browser settings.',
  connection_failed_title: 'Connection Failed',
  connection_failed_message: 'Failed to start conversation.',
  connection_error_title: 'Connection Error',
  connection_error_message: 'Failed to connect to voice agent.',
  greeting_with_agent: "Hey there! I'm {{agentName}}. How can I help you today?",
  greeting_without_agent: 'Hey there! How can I help you today?',
};

const TEXT_CONTENT_TRANSLATIONS: Record<SupportedLanguage, TextContent> = {
  en: DEFAULT_TEXT_CONTENT,
  ar: {
    ...DEFAULT_TEXT_CONTENT,
    main_label: 'تحتاج مساعدة؟',
    start_call: 'بدء مكالمة',
    start_chat: 'بدء محادثة',
    new_call: 'مكالمة جديدة',
    end_call: 'إنهاء',
    mute_microphone: 'كتم الميكروفون',
    change_language: 'تغيير اللغة',
    collapse: 'طي',
    expand: 'توسيع',
    copied: 'تم النسخ!',
    accept_terms: 'موافقة',
    dismiss_terms: 'إلغاء',
    listening_status: 'يستمع',
    speaking_status: 'تحدث للمقاطعة',
    connecting_status: 'جارٍ الاتصال',
    chatting_status: 'يتحدث مع وكيل الذكاء الاصطناعي',
    input_label: 'إدخال الرسالة النصية',
    input_placeholder: 'أرسل رسالة',
    input_placeholder_text_only: 'أرسل رسالة',
    input_placeholder_new_conversation: 'ابدأ محادثة جديدة',
    user_ended_conversation: 'لقد أنهيت المحادثة',
    agent_ended_conversation: 'أنهى الوكيل المحادثة',
    conversation_id: 'رقم المحادثة',
    error_occurred: 'حدث خطأ',
    microphone_access_required: 'يتطلب إذن الميكروفون',
    copy_id: 'نسخ الرقم',
    microphone_blocked_message: 'تم حظر الوصول إلى الميكروفون. يرجى النقر على رمز الميكروفون في شريط عنوان المتصفح والسماح بالوصول ثم أعد تحميل الصفحة.',
    microphone_unavailable_title: 'الميكروفون غير متوفر',
    microphone_unavailable_message: 'الميكروفون غير متوفر. يرجى التأكد من أنه متصل ومفعّل ثم حاول مرة أخرى.',
    refresh_button_label: 'تحديث الصفحة',
    input_device_label: 'جهاز الميكروفون',
    input_device_label_connected: 'الإدخال',
    output_device_label: 'جهاز السماعة',
    output_device_label_connected: 'الإخراج',
    select_device_placeholder: 'اختر جهازاً',
    input_device_changed_title: 'تم تغيير الجهاز',
    input_device_changed_message: 'تم تبديل جهاز الإدخال بنجاح.',
    input_device_change_failed_title: 'فشل تغيير الجهاز',
    input_device_change_failed_message: 'تعذر تبديل جهاز الإدخال.',
    output_device_changed_title: 'تم تغيير الجهاز',
    output_device_changed_message: 'تم تبديل جهاز الإخراج بنجاح.',
    output_device_change_failed_title: 'فشل تغيير الجهاز',
    output_device_change_failed_message: 'تعذر تبديل جهاز الإخراج.',
    send_failed_title: 'فشل الإرسال',
    send_not_supported_message: 'لا يمكن إرسال الرسائل النصية أثناء المكالمة الصوتية.',
    send_failed_generic_message: 'تعذر إرسال الرسالة. يرجى المحاولة لاحقاً.',
    microphone_muted_title: 'تم كتم الميكروفون',
    microphone_muted_message: 'تم كتم الميكروفون. يرجى التحقق من إعدادات المتصفح.',
    connection_failed_title: 'فشل الاتصال',
    connection_failed_message: 'تعذر بدء المحادثة.',
    connection_error_title: 'خطأ في الاتصال',
    connection_error_message: 'تعذر الاتصال بالوكيل الصوتي.',
    greeting_with_agent: 'مرحباً! أنا {{agentName}}. كيف يمكنني مساعدتك اليوم؟',
    greeting_without_agent: 'مرحباً! كيف يمكنني مساعدتك اليوم؟',
  },
};

export const getWidgetTextContent = (language: SupportedLanguage): TextContent =>
  TEXT_CONTENT_TRANSLATIONS[language] || DEFAULT_TEXT_CONTENT;

// Predefined theme presets
export const THEME_PRESETS: Record<string, ThemeConfig> = {
  default: DEFAULT_THEME,
  dark: {
    colors: {
      base: '#1f2937',
      base_hover: '#374151',
      base_active: '#4b5563',
      base_border: '#6b7280',
      base_subtle: '#9ca3af',
      base_primary: '#ffffff',
      base_error: '#f87171',
      accent: '#3b82f6',
      accent_hover: '#2563eb',
      accent_active: '#1d4ed8',
      accent_border: '#1e40af',
      accent_subtle: '#60a5fa',
      accent_primary: '#ffffff',
      avatar_first_color: '#3b82f6',
      avatar_second_color: '#60a5fa',
    },
    spacing: DEFAULT_THEME.spacing,
    typography: DEFAULT_THEME.typography,
  },
  purple: {
    colors: {
      base: '#ffffff',
      base_hover: '#f9fafb',
      base_active: '#f3f4f6',
      base_border: '#e5e7eb',
      base_subtle: '#6b7280',
      base_primary: '#000000',
      base_error: '#ef4444',
      accent: '#8b5cf6',
      accent_hover: '#7c3aed',
      accent_active: '#6d28d9',
      accent_border: '#5b21b6',
      accent_subtle: '#a78bfa',
      accent_primary: '#ffffff',
      avatar_first_color: '#8b5cf6',
      avatar_second_color: '#a78bfa',
    },
    spacing: DEFAULT_THEME.spacing,
    typography: DEFAULT_THEME.typography,
  },
  green: {
    colors: {
      base: '#ffffff',
      base_hover: '#f9fafb',
      base_active: '#f3f4f6',
      base_border: '#e5e7eb',
      base_subtle: '#6b7280',
      base_primary: '#000000',
      base_error: '#ef4444',
      accent: '#10b981',
      accent_hover: '#059669',
      accent_active: '#047857',
      accent_border: '#065f46',
      accent_subtle: '#34d399',
      accent_primary: '#ffffff',
      avatar_first_color: '#10b981',
      avatar_second_color: '#34d399',
    },
    spacing: DEFAULT_THEME.spacing,
    typography: DEFAULT_THEME.typography,
  },
  orange: {
    colors: {
      base: '#ffffff',
      base_hover: '#f9fafb',
      base_active: '#f3f4f6',
      base_border: '#e5e7eb',
      base_subtle: '#6b7280',
      base_primary: '#000000',
      base_error: '#ef4444',
      accent: '#f59e0b',
      accent_hover: '#d97706',
      accent_active: '#b45309',
      accent_border: '#92400e',
      accent_subtle: '#fbbf24',
      accent_primary: '#ffffff',
      avatar_first_color: '#f59e0b',
      avatar_second_color: '#fbbf24',
    },
    spacing: DEFAULT_THEME.spacing,
    typography: DEFAULT_THEME.typography,
  },
};

export const mergeThemeConfig = (
  base: ThemeConfig,
  overrides?: Partial<ThemeConfig>
): ThemeConfig => {
  if (!overrides) {
    return base;
  }

  const mergedColors = overrides.colors
    ? { ...base.colors, ...overrides.colors }
    : base.colors;

  const mergedSpacing = overrides.spacing
    ? { ...base.spacing, ...overrides.spacing }
    : base.spacing;

  const mergedTypography = overrides.typography
    ? { ...base.typography, ...overrides.typography }
    : base.typography;

  const mergedOptions = overrides.options || base.options
    ? { ...(base.options || {}), ...(overrides.options || {}) }
    : undefined;

  return {
    colors: mergedColors,
    spacing: mergedSpacing,
    typography: mergedTypography,
    options: mergedOptions,
  };
};
